import { GamIntegration } from '@pubstack/common/src/gam/adx'
import { Feature } from '@pubstack/common/src/rolesAndFeatures'
import { AdminScopeDataIngestionFlags, EMPTY_ADMIN_SCOPE } from '@pubstack/common/src/scope'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent } from 'react'
import { useParams } from 'react-router-dom'
import { useAdminScope, useUpdateScope, useUpdateScopeFeature } from '~/api/admin/admin-scope.api'
import { useGamIntegration } from '~/api/admin/gam-integration.api'
import { useToast } from '~/components/Toast/useToasts'
import { toggleValueInArray } from '~/utils/array'
import { AdminScopeFeaturesGamIntegration } from './AdminScopeFeaturesGamIntegration'
import { AdminScopeTestBidder } from './AdminScopeTestBidder'
import { PureAdminScopeFeatures } from './PureAdminScopeFeatures'

const EMPTY_GAM_INTEGRATION: GamIntegration = {
  serviceAccount: undefined,
  secretName: undefined,
  gamNetworks: [],
}

export const AdminScopeFeatures: FunctionComponent = () => {
  // Context
  const { scopeId } = useParams()

  // API hooks
  const { data: scope, isLoading: isScopeLoading } = useAdminScope(scopeId)
  const { data: gamIntegration, isLoading: isGamIntegrationLoading } = useGamIntegration(scopeId)

  // Mutations
  const { mutateAsync: updateScope, isPending: isScopeUpdatePending } = useUpdateScope(scopeId ?? '')
  const { mutateAsync: updateScopeFeatures, isPending: isScopeFeatureUpdatePending } = useUpdateScopeFeature(scopeId ?? '', {
    errorToast: 'An error occurred while saving the features of this scope.',
  })

  // Hooks
  const toast = useToast()
  const queryClient = useQueryClient()

  const currentIntegration = gamIntegration ?? EMPTY_GAM_INTEGRATION

  //TODO tmu nra 2022-09-21 -- debounce this so we don't flood the api
  const toggleFeature = async (featureName: Feature) => {
    if (scope) {
      let enableUserSessionIngestion = false
      if (featureName === 'adstack' && !scope.features?.includes(featureName)) {
        toast.alert(`Please make sure a configuration has been created for the scope ${scope.name} before enabling the Ad Management feature.`, { icon: 'rocket', duration: Infinity })
        // if adstack is enabled, we want to enable user session ingestion as well
        if (!scope.standaloneUserSessionEnabled) {
          toast.success(`User sessions data ingestion will also be enabled.`, { icon: 'info', duration: Infinity })
          enableUserSessionIngestion = true
        }
      }
      const updatedScope = {
        ...scope,
        standaloneUserSessionEnabled: scope.standaloneUserSessionEnabled || enableUserSessionIngestion,
      }
      // To use optimistic updates, we need to update the query data before the mutation
      queryClient.setQueryData(['admin', 'scopes', scopeId], {
        ...updatedScope,
        features: scope.features?.includes(featureName) ? scope.features?.filter((f) => f !== featureName) : [...(scope?.features ?? []), featureName],
      })

      await updateScope(updatedScope)
      await updateScopeFeatures(scope.features?.includes(featureName) ? scope.features?.filter((f) => f !== featureName) : [...(scope?.features ?? []), featureName])
    }
  }

  const toggleFeatureOnboarding = async (featureName: Feature) => {
    if (!scope) return

    const updatedScope = {
      ...scope,
      onboardedFeatures: toggleValueInArray(scope.onboardedFeatures ?? [], featureName),
    }
    await updateScope(updatedScope)
  }

  //TODO tmu nra 2022-09-21 -- debounce this so we don't flood the api
  const toggleDataIngestion = async (flagName: keyof AdminScopeDataIngestionFlags) => {
    if (scope) {
      // user sessions must remain enabled if adstack is enabled
      if (flagName === 'standaloneUserSessionEnabled' && scope.standaloneUserSessionEnabled && scope.features?.includes('adstack')) {
        toast.alert(`You cannot disable the user sessions data ingestion while the Ad Management feature is enabled.`, { icon: 'warning', duration: Infinity })
        return
      }
      const updatedScope = {
        ...scope,
        [flagName]: !scope[flagName],
      }
      await updateScope(updatedScope)
    }
  }

  const gamIngestionStatus: { adxEnabled: boolean; openBiddingEnabled: boolean; gamIntegrationEnabled: boolean } = {
    adxEnabled: (currentIntegration.gamNetworks ?? []).some((n) => n.dataIngestionStatus === 'ACTIVE') ?? false,
    openBiddingEnabled: (currentIntegration.gamNetworks ?? []).some((n) => n.openBiddingEnabled) ?? false,
    gamIntegrationEnabled: (currentIntegration.gamNetworks ?? []).some((n) => n.gamIntegrationEnabled) ?? false,
  }

  //TODO tmu nra 2022-09-21 -- handle error
  return (
    <>
      <PureAdminScopeFeatures
        isLoading={isScopeLoading || isScopeUpdatePending || isScopeFeatureUpdatePending || isGamIntegrationLoading}
        gamCollapsibleContent={scope && <AdminScopeFeaturesGamIntegration scope={scope} currentIntegration={currentIntegration} gamIntegrationLoading={isGamIntegrationLoading} />}
        gamIngestion={gamIngestionStatus}
        scope={scope ?? EMPTY_ADMIN_SCOPE}
        toggleFeature={toggleFeature}
        toggleDataIngestion={toggleDataIngestion}
        toggleFeatureOnboarding={toggleFeatureOnboarding}
      />
      {scopeId && scope && (
        <div className="pb-6">
          <AdminScopeTestBidder scopeId={scopeId} />
        </div>
      )}
    </>
  )
}
